/**
 * OIDC Configuration for OneLogin
 * 
 * This file configures the OIDC client for authentication with OneLogin.
 */

import type { AuthProviderProps } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import { config } from '../config';

/**
 * OIDC configuration for react-oidc-context
 */
export const oidcConfig: AuthProviderProps = {
  authority: config.oidc.authority,
  client_id: config.oidc.clientId,
  redirect_uri: config.oidc.redirectUri || `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: config.oidc.postLogoutRedirectUri || window.location.origin,
  
  // Request these scopes from OneLogin
  // 'params' scope includes custom claims like user_access_role
  scope: 'openid profile email params',
  
  // Use PKCE for public clients (SPA)
  // response_type defaults to 'code' which is correct for PKCE
  
  // Persist tokens in localStorage so that an existing session is reused
  // across tabs and browser restarts ("auto-login"). On app start
  // react-oidc-context will pick up the stored user and, if the access/ID
  // token has expired, silently renew it against OneLogin via an iframe
  // (see automaticSilentRenew below). Only short-lived signing state stays
  // in sessionStorage so a redirect flow started in one tab doesn't leak
  // into another.
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),

  // Automatically sign in silently when the token expires.
  // Combined with the localStorage userStore above this gives us a true
  // auto-login: as long as the OneLogin SSO session is still valid the
  // user never sees a login prompt again.
  automaticSilentRenew: true,
  // Keep the id_token (with our custom `params` claim) up to date on renew.
  includeIdTokenInSilentRenew: true,
  
  // Handle the callback - clean up URL and let the app handle navigation
  onSigninCallback: () => {
    // Remove the OIDC callback parameters from the URL
    // The AuthCallbackPage component will handle the actual navigation
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};
